import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-menu-heading" }
const _hoisted_2 = { class: "w-small" }
const _hoisted_3 = ["data-color", "disabled", "aria-label", "onClick"]
const _hoisted_4 = { class: "w-option-title" }
const _hoisted_5 = { class: "w-option-sub" }
const _hoisted_6 = {
  key: 0,
  class: "w-menu-note"
}

import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import { formatDate } from '~utils/calendarModel';
import type { Availability } from '@/store/calendar/types';

type RoomMenuProps = {
  container: HTMLElement | null;
};

const MENU_MAX_WIDTH = 300;
const MENU_GAP = 7;
const EDGE_GAP = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'RoomMenu',
  props: {
    container: {}
  },
  setup(__props: any) {

const props = __props;

const store = useScheduleStore();
const menu = ref<HTMLElement | null>(null);

const slot = computed(() => (
  store.schedule.slots.find((item) => item.id === store.roomMenuSlotId) || null
));

const hasPatient = computed(() => (
  store.schedule.appointments.some((item) => item.slotId === slot.value?.id)
));

const describe = (availability: Availability): string => {
  if (availability.color === 'red') return 'Есть запись пациента';
  if (availability.branchBlocked) return availability.branchReason;
  if (availability.color === 'gray') {
    return `Закрыт: ${availability.roomBreaks[0]?.label || 'перерыв'}`;
  }
  if (availability.color === 'yellow') {
    return `${availability.staffIds.map(store.lastName).join(', ')} · без пациентов`;
  }
  return 'Свободен для вас';
};

const options = computed(() => {
  const current = slot.value;
  if (!current) return [];
  return store.allowedRooms.map((room) => {
    const availability = store.slotAvailability(current, room.id);
    const isCurrent = room.id === current.roomId;
    const number = room.number ?? room.id;
    const description = describe(availability);
    return {
      room,
      number,
      current: isCurrent,
      color: availability.color,
      description,
      ariaLabel: `Кабинет ${number}. ${description}${isCurrent ? '. Выбран' : ''}`,
      // Занять другой кабинет нельзя, если он занят или на приём уже записан пациент
      disabled: !store.canEdit || (!isCurrent && (availability.blocked || hasPatient.value)),
    };
  });
});

// Меню открывается под кнопкой кабинета, а если снизу не помещается — над ней
const placeMenu = () => {
  const element = menu.value;
  const anchor = props.container?.querySelector(`[data-room-slot="${store.roomMenuSlotId}"]`);
  if (!element || !props.container || !anchor) return;
  const box = props.container.getBoundingClientRect();
  const button = anchor.getBoundingClientRect();
  element.style.width = `${Math.min(MENU_MAX_WIDTH, box.width - 24)}px`;
  const { offsetWidth: width, offsetHeight: height } = element;
  const below = button.bottom - box.top + MENU_GAP;
  const above = button.top - box.top - height - MENU_GAP;
  const left = Math.min(button.left - box.left, box.width - width - EDGE_GAP);
  element.style.left = `${Math.max(EDGE_GAP, left)}px`;
  element.style.top = `${Math.max(8, below + height < box.height ? below : above)}px`;
};

watch(() => store.roomMenuSlotId, async (id) => {
  if (id == null) return;
  await nextTick();
  placeMenu();
});

const closeOnOutsideClick = (event: PointerEvent) => {
  if (store.roomMenuSlotId == null) return;
  const target = event.target as HTMLElement;
  if (menu.value?.contains(target) || target.closest?.('[data-room-slot]')) return;
  store.closeRoomMenu();
};

onMounted(() => document.addEventListener('pointerdown', closeOnOutsideClick));
onBeforeUnmount(() => document.removeEventListener('pointerdown', closeOnOutsideClick));

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!

  return (slot.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "menu",
        ref: menu,
        class: "w-room-menu",
        "aria-label": "Выбор кабинета"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("strong", null, "Кабинет на " + _toDisplayString(slot.value.start) + "–" + _toDisplayString(slot.value.end), 1),
          _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(formatDate)(slot.value.date, { day: 'numeric', month: 'long' })), 1)
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option) => {
          return (_openBlock(), _createElementBlock("button", {
            key: option.room.id,
            type: "button",
            class: _normalizeClass(["w-room-option", { 'is-selected': option.current }]),
            "data-color": option.color,
            disabled: option.disabled,
            "aria-label": option.ariaLabel,
            onClick: ($event: any) => (_unref(store).changeRoom(option.room.id))
          }, [
            _createElementVNode("span", _hoisted_4, [
              _createElementVNode("strong", null, _toDisplayString(option.number), 1),
              _createElementVNode("span", null, _toDisplayString(option.room.purpose), 1),
              (option.current)
                ? (_openBlock(), _createBlock(_component_q_icon, {
                    key: 0,
                    name: "check"
                  }))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("span", _hoisted_5, _toDisplayString(option.description), 1)
          ], 10, _hoisted_3))
        }), 128)),
        (hasPatient.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Для смены кабинета сначала отмените запись пациента. "))
          : _createCommentVNode("", true)
      ], 512))
    : _createCommentVNode("", true)
}
}

})